/**
 * Serialization utilities for PlateEmailEditor.
 *
 * These functions convert between Plate.js node structures and HTML strings.
 * Plate.js wraps list item content in nested paragraph/lic nodes, so we need
 * to recursively handle these structures when serializing.
 */
import {
  emitSlotTag,
  MEETING_SLOT_NO_ID_RE,
  MEETING_SLOT_TAG_WITH_ID_RE,
} from '@/lib/email/meetingSlotTag';

/**
 * Escapes HTML special characters in text content to prevent XSS and broken markup.
 */
export const escapeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

/**
 * Render a plain-text body as its HTML alternative — the inverse of
 * `htmlToPlainText`, and the client mirror of the backend's
 * `apps/utils/text_utils.py::plain_to_html`. A blank line is a paragraph
 * boundary (`</p><p>`); a single newline is a line break (`<br>`). Escaping is
 * mandatory: the text is user/AI content going into markup, so a bare `<`
 * would break the message. Returns `''` for empty input.
 */
export const plainToHtml = (text: string): string => {
  if (!text) {
    return '';
  }
  const body = escapeHtml(text)
    .replace(/\n\s*\n/g, '</p><p>')
    .replace(/\n/g, '<br>\n');
  return body.trim() ? `<p>${body}</p>` : body;
};

// Delimiters wrapping a slot id, swapped in before the DOM parse and restored to
// the canonical void tag after. Built from private-use unicode code points
// (U+E000 / U+E001) so they can never collide with author content or survive
// into the final output. Constructed via fromCharCode to keep the source ASCII.
const SLOT_TOKEN_OPEN = String.fromCharCode(0xe000);
const SLOT_TOKEN_CLOSE = String.fromCharCode(0xe001);
// Strips any pre-existing delimiter chars from the input before we insert our
// own pairs. Author content should never contain these private-use code points,
// but if it did, restoreMeetingSlotTokens would later mistake that text for a
// slot reference and substitute (or fail) the send. They carry no legitimate
// meaning in email text and would be dropped from output anyway, so removing
// them up front guarantees the only delimiters present are the ones we insert.
const SLOT_TOKEN_STRIP_RE = new RegExp(
  `[${SLOT_TOKEN_OPEN}${SLOT_TOKEN_CLOSE}]`,
  'g'
);

// Non-greedy id capture between the two PUA delimiters.
const SLOT_TOKEN_RE = new RegExp(
  `${SLOT_TOKEN_OPEN}(.*?)${SLOT_TOKEN_CLOSE}`,
  'g'
);

function replaceMeetingSlotTagsWithTokens(html: string): string {
  return (
    html
      // Remove any delimiters the author content already carries so they can't be
      // resurrected as a spurious slot reference by restoreMeetingSlotTokens.
      .replace(SLOT_TOKEN_STRIP_RE, '')
      .replace(
        MEETING_SLOT_TAG_WITH_ID_RE,
        // Re-emit the paired tag's inner text after the token so it survives to the
        // plain body — matching the backend substitute_slot_ref, which appends any
        // folded inner text after the rendered time rather than dropping it.
        (_match, id: string, inner: string | undefined) =>
          `${SLOT_TOKEN_OPEN}${id}${SLOT_TOKEN_CLOSE}${inner ?? ''}`
      )
      .replace(
        MEETING_SLOT_NO_ID_RE,
        (_match, inner: string | undefined) => inner ?? ''
      )
  );
}

function restoreMeetingSlotTokens(text: string): string {
  return text.replace(SLOT_TOKEN_RE, (_match, id: string) => emitSlotTag(id));
}

/**
 * Derive the text/plain alternative from an HTML email body.
 *
 * An email carries two representations of the same message: `body_html` (the
 * HTML part) and `body` (the text/plain part). They are NOT the same string —
 * putting markup in the plain part means recipients on a plain-text client, and
 * anything else that reads `body`, see raw `<p>` tags.
 *
 * Block boundaries and `<br>` become newlines so the plain part keeps the
 * message's shape. Adjacent table cells (`<td>`/`<th>`) are separated by a
 * space so a logo cell and a name cell don't fuse into one word. `<img>`
 * elements are replaced by their `alt` text so signature images contribute
 * their author-provided description instead of vanishing. `<meeting-slot>`
 * chips are void reference tags carrying no text, so `textContent` would drop
 * the proposed time entirely — we preserve the literal `<meeting-slot id="…"/>`
 * tag as text so the backend send-time substitution (`substitute_slot_ref` in
 * apps/email/actions/rendering.py, which runs over the plain `body` too) still
 * resolves it to the real time instead of the recipient seeing a gap where the
 * meeting time should be. Runs of blank lines are collapsed — matching what the
 * backend's `html_to_plain_text` produces for the same input.
 */
export const htmlToPlainText = (html: string): string => {
  // Rewrite <meeting-slot> chips to a plain-text sentinel token BEFORE the DOM
  // parse. HTML5 has no self-closing syntax for unknown elements, so the parser
  // would rewrite `<meeting-slot id="X"/>` into an OPEN tag and fold every
  // following sibling ("… instead?") in as its children — then a textContent
  // read would erase the tag along with that text. Substituting first sidesteps
  // the fold entirely and restores the canonical `<meeting-slot id="X"/>` after,
  // so the backend's send-time substitution resolves it for plain-text
  // recipients. Mirrors the backend's restore_void_reference_tags.
  const withSlotTokens = replaceMeetingSlotTagsWithTokens(html);

  const container = document.createElement('div');
  // Convert structure to newlines BEFORE reading textContent, which would
  // otherwise run every block together into a single line. Block elements get
  // a blank line between them; <br> and list items are single line breaks;
  // adjacent table cells (td/th) are separated by a space so a logo cell and
  // a name cell don't fuse.
  container.innerHTML = withSlotTokens
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(td|th)>/gi, ' ')
    .replace(/<\/(li|tr)>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|blockquote|ul|ol|table)>/gi, '\n\n');
  // Replace each <img> with its alt text so a standalone or linked logo image
  // contributes its author-provided description instead of vanishing.
  container.querySelectorAll('img').forEach(img => {
    img.replaceWith(document.createTextNode(img.getAttribute('alt') ?? ''));
  });
  const text = restoreMeetingSlotTokens(container.textContent ?? '');
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Escapes characters for use in HTML attributes (href, etc.).
 * Handles quotes in addition to standard HTML entities.
 */
export const escapeAttribute = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const SAFE_URL_PATTERN = /^(https?:\/\/|mailto:|tel:|\/|#)/i;

export const sanitizeUrl = (url: string): string => {
  // eslint-disable-next-line no-control-regex
  const normalizedUrl = url.trim().replace(/[\x00-\x1f\x7f]/g, '');
  if (!normalizedUrl) {
    return '';
  }
  if (SAFE_URL_PATTERN.test(normalizedUrl)) {
    return normalizedUrl;
  }
  // Block dangerous schemes like javascript:, data:, vbscript:
  if (/^[a-z][a-z0-9+.-]*:/i.test(normalizedUrl)) {
    return '';
  }
  return normalizedUrl;
};

export interface PlateTextNode {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface PlateLinkNode {
  type: 'a';
  url: string;
  children: PlateTextNode[];
}

export interface PlateBlockNode {
  type: 'p' | 'lic' | 'h1' | 'h2' | 'h3' | 'blockquote';
  children: Array<
    PlateTextNode | PlateLinkNode | PlateBlockNode | PlateSlotNode
  >;
}

export interface PlateListItemNode {
  type: 'li';
  children: Array<
    PlateTextNode | PlateLinkNode | PlateBlockNode | PlateSlotNode
  >;
}

export interface PlateListNode {
  type: 'ul' | 'ol';
  children: PlateListItemNode[];
}

export interface PlateImageNode {
  type: 'img';
  url: string;
  alt?: string;
  fileId?: number;
  width?: 'small' | 'medium' | 'large' | number;
}

export interface PlateSignatureNode {
  type: 'signature';
  html: string;
  children: { text: string }[];
}

export interface PlateSlotNode {
  type: 'slot';
  slotId: string;
  label?: string;
  labelTz?: string;
  startIso?: string;
  resolutionAttempted?: boolean;
  resolutionFailed?: boolean;
  children: { text: string }[];
}

export type PlateNode =
  | PlateTextNode
  | PlateLinkNode
  | PlateBlockNode
  | PlateListNode
  | PlateListItemNode
  | PlateImageNode
  | PlateSignatureNode
  | PlateSlotNode;

/**
 * Serializes inline content (text, links, formatting) to HTML.
 *
 * This function handles nested block nodes (p, lic) that Plate.js wraps list item
 * content in. Without recursively handling these, list items with links would
 * serialize as empty `<li></li>` elements.
 *
 * Example of Plate.js structure for a list item with a link:
 * {
 *   type: 'li',
 *   children: [{
 *     type: 'lic',  // or 'p' in some cases
 *     children: [{
 *       type: 'a',
 *       url: 'https://example.com',
 *       children: [{ text: 'Example Link' }]
 *     }]
 *   }]
 * }
 *
 * @param children - Array of Plate.js child nodes
 * @returns HTML string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate node tree serialization operates on untyped node structures with mixed element types
export const serializeInlineContent = (children: any[]): string => {
  if (!children) {
    return '';
  }
  return (
    children
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate node tree serialization operates on untyped node structures with mixed element types
      .map((child: any) => {
        // Handle slot chip nodes - emit only the self-closing <meeting-slot/>
        // reference form the send path resolves. label/startIso are
        // display-only editor state and must never be serialized: emitting
        // them would diverge from the send reference form and churn
        // autosave versions.
        if (child.type === 'slot') {
          if (child.slotId) {
            return emitSlotTag(escapeAttribute(child.slotId));
          }
          // A failed chip with no id (from <meeting-slot/> with no id attribute) must
          // round-trip as <meeting-slot/> — not empty — so the backend send-time guard
          // (find_malformed_slot_refs) can still detect and block it.
          if (child.resolutionFailed) {
            return '<meeting-slot/>';
          }
          return '';
        }

        // Handle link nodes - use recursive serialization to preserve formatting
        if (child.type === 'a') {
          const linkText = serializeInlineContent(child.children || []);
          const url = escapeAttribute(sanitizeUrl(child.url || ''));
          if (!url) {
            return linkText;
          }
          return `<a href="${url}">${linkText}</a>`;
        }

        // Handle nested list nodes - delegate back to serializePlateToHtml so the
        // surrounding <ul>/<ol> wrappers are preserved. Without this branch the
        // catch-all on the next clause silently unwraps nested lists.
        if (child.type === 'ul' || child.type === 'ol') {
          return serializePlateToHtml([child]);
        }

        // Handle nested block nodes (p, lic, etc.) - recurse into their children
        // This is critical for list items which Plate.js wraps in paragraph/lic nodes
        if (
          child.type === 'p' ||
          child.type === 'lic' ||
          (child.children && !child.text)
        ) {
          return serializeInlineContent(child.children);
        }

        // Handle text nodes - escape HTML first, then apply formatting
        let text = escapeHtml(child.text || '');
        text = text.replace(/\n/g, '<br>');
        if (child.bold) {
          text = `<strong>${text}</strong>`;
        }
        if (child.italic) {
          text = `<em>${text}</em>`;
        }
        if (child.underline) {
          text = `<u>${text}</u>`;
        }
        return text;
      })
      .join('')
  );
};

/**
 * Converts image width to a CSS width value.
 */
const getImageWidthStyle = (
  width: 'small' | 'medium' | 'large' | number | undefined
): string => {
  if (width == null) {
    return '400px';
  }
  if (typeof width === 'number') {
    return `${width}px`;
  }
  switch (width) {
    case 'small':
      return '200px';
    case 'medium':
      return '400px';
    case 'large':
      return '100%';
    default:
      return '400px';
  }
};

/**
 * Serializes a Plate.js Value (array of nodes) to HTML.
 *
 * @param nodes - Array of Plate.js nodes
 * @returns HTML string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate node tree serialization operates on untyped node structures with mixed element types
export const serializePlateToHtml = (nodes: any[]): string => {
  return (
    nodes
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate node tree serialization operates on untyped node structures with mixed element types
      .map((node: any) => {
        // Opaque signature block — emit its stored HTML verbatim. Reverse the
        // parser's file:ID -> about:blank sanitization so file-backed images inside
        // the block round-trip to their file:ID src (mirrors the img void node).
        if (node.type === 'signature') {
          const html = typeof node.html === 'string' ? node.html : '';
          return html.replace(
            /src=["']about:blank["']\s+data-file-id=["'](\d+)["']/gi,
            'src="file:$1" data-file-id="$1"'
          );
        }

        // Handle image nodes (void elements, no children required)
        // Wrap in <p> to ensure images are block-level and stack vertically
        if (node.type === 'img') {
          const alt = escapeAttribute(node.alt || '');
          const width = getImageWidthStyle(node.width);
          // Skip images that are still uploading or have errors
          if (node.uploading || node.uploadError) {
            return '';
          }
          // Use file:ID format for backend processing when fileId is present
          // Otherwise fall back to the URL (for backwards compatibility).
          // data-file-id duplicates the id in a sanitizer-safe attribute: the
          // crm_events write path (event_normalizer.py) strips the file: src
          // (unlisted URL scheme) but allowlists data-file-id, so editor
          // images survive storage in CRM properties (signatures, canned
          // responses) and parse-html-to-plate-value restores them from it.
          if (node.fileId) {
            return `<p><img src="file:${node.fileId}" data-file-id="${node.fileId}" alt="${alt}" style="width: ${width}; max-width: 100%; height: auto; display: block;" /></p>`;
          }
          const src = escapeAttribute(sanitizeUrl(node.url || ''));
          return `<p><img src="${src}" alt="${alt}" style="width: ${width}; max-width: 100%; height: auto; display: block;" /></p>`;
        }

        if (!node.children) {
          return '';
        }

        // Handle list nodes (ul/ol) differently
        if (node.type === 'ul' || node.type === 'ol') {
          const listItems = node.children
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate node tree serialization operates on untyped node structures with mixed element types
            .map((listItem: any) => {
              if (listItem.type !== 'li') {
                return '';
              }

              // Use helper to handle nested content structures
              const itemContent = serializeInlineContent(listItem.children);

              return `<li>${itemContent}</li>`;
            })
            .join('');

          return node.type === 'ul'
            ? `<ul>${listItems}</ul>`
            : `<ol>${listItems}</ol>`;
        }

        // Use helper for consistent serialization
        const textContent = serializeInlineContent(node.children);

        // Wrap in appropriate HTML tag based on node type
        switch (node.type) {
          case 'h1':
            return `<h1>${textContent}</h1>`;
          case 'h2':
            return `<h2>${textContent}</h2>`;
          case 'h3':
            return `<h3>${textContent}</h3>`;
          case 'blockquote':
            return `<blockquote>${textContent}</blockquote>`;
          case 'p':
          default:
            return `<p>${textContent}</p>`;
        }
      })
      .join('')
  );
};
